/**
 * «Hoy» in «Análisis» (the owner, 2026-10-13, of what the page could add: «let's build all of it»):
 * one list of what to do, what puts the most money at stake first, each opening the screen that
 * does it; and how far the figures can be believed. Owners act on what they are told more than on
 * charts they browse, so this is the tab the page opens on.
 *
 * Each kind is something another screen already answers: «Por comprar», the prices under the
 * margin, «Vencimientos», «Fiado», «Control», «Transferencias», and the other tabs. What is new is
 * the money each puts at stake, so they can stand in one order. Pure, so the screen and its tests
 * share one answer.
 */

import { costOf, diasEntre, sumarDias, type Centavos, type Milli, type UnitCost } from '@abastece/shared';
import { netosDeLineas, type Conteo, type Existencia, type Producto, type Venta } from '@abastece/core';
import { diasDeRitmo, vendidoPorProducto, type Agotado } from './estante.js';
import type { PrecioBajo } from './margenes.js';
import { VENTANA_DIAS, type Sugerencia } from './reponer.js';

/** The kinds, in the order they go when they put as much at stake. */
export const TIPOS_DE_DECISION = ['dinero', 'reponer', 'vencen', 'limite', 'fiadoViejo', 'faltantes', 'transferencias', 'margen', 'lesToca', 'barato', 'sobra'] as const;
export type TipoDecision = (typeof TIPOS_DE_DECISION)[number];

export interface Decision {
  tipo: TipoDecision;
  /** What is at stake, in centavos: what it is, each kind says («a la semana», «al mes», «en mercadería»…). */
  monto: Centavos;
  /** How many: products, prices, clients, shortages, transfers. */
  n: number;
  /** The first few by what each puts at stake, for the sentence. */
  nombres: string[];
  /** The day, for the money that would run short. */
  fecha?: string;
}

/** How many names a decision says before «y 3 más». */
export const NOMBRES_DICHOS = 3;

/** What is out or running out puts a week of its sales at stake: about what a purchase takes to arrive. */
export const DIAS_EN_JUEGO = 7;

/** The days a low price is weighed over: what it sold the last thirty, as «al mes». */
export const DIAS_DEL_MARGEN = 30;

/** A kind's rows as one thing to decide: what they put at stake together, how many, the first few by name. */
export function decisionDe(tipo: TipoDecision, filas: readonly { nombre: string; monto: number }[], extra: { fecha?: string } = {}): Decision {
  const orden = [...filas].sort((a, b) => b.monto - a.monto || a.nombre.localeCompare(b.nombre, 'es'));
  return {
    tipo,
    monto: Math.round(orden.reduce((a, f) => a + f.monto, 0)) as Centavos,
    n: filas.length,
    nombres: orden.slice(0, NOMBRES_DICHOS).map((f) => f.nombre),
    ...(extra.fecha ? { fecha: extra.fecha } : {}),
  };
}

/** The things to decide in one order: the most at stake first, a kind before another as `TIPOS_DE_DECISION` has them; none with nothing in it. */
export function ordenarDecisiones(decisiones: readonly (Decision | null)[]): Decision[] {
  return decisiones
    .filter((d): d is Decision => d !== null && d.n > 0)
    .sort((a, b) => b.monto - a.monto || TIPOS_DE_DECISION.indexOf(a.tipo) - TIPOS_DE_DECISION.indexOf(b.tipo));
}

// ---------------------------------------------------------------------------
// What each kind puts at stake
// ---------------------------------------------------------------------------

/**
 * What ran out and what is running out, with what each sold a day: what ran out at what it sold
 * the four weeks before (`agotados`), what is running out (`seEstaAcabando` of «Por comprar») at
 * what it sold the last four weeks, from the shop's first sale if later. What sold nothing puts
 * nothing at stake and is not here: «Por comprar» still has it. What sells most a day first.
 */
export function queSeAcaba(d: {
  agotados: readonly Agotado[];
  acabandose: readonly Sugerencia[];
  ventas: readonly Venta[];
  hoy: string;
  primeraVenta: string | null;
}): { productoId: string; nombre: string; alDia: number }[] {
  const lista = new Map<string, { productoId: string; nombre: string; alDia: number }>();
  for (const a of d.agotados) if (a.ventaAlDia > 0) lista.set(a.productoId, { productoId: a.productoId, nombre: a.nombre, alDia: a.ventaAlDia });
  const desde = sumarDias(d.hoy, -(VENTANA_DIAS - 1));
  const vendido = vendidoPorProducto(d.ventas, desde, d.hoy);
  const dias = diasDeRitmo(desde, d.hoy, d.primeraVenta);
  for (const s of d.acabandose) {
    if (lista.has(s.producto.id)) continue;
    const alDia = (vendido.get(s.producto.id)?.ingreso ?? 0) / dias;
    if (alDia > 0) lista.set(s.producto.id, { productoId: s.producto.id, nombre: s.producto.nombre, alDia });
  }
  return [...lista.values()].sort((a, b) => b.alDia - a.alDia || a.nombre.localeCompare(b.nombre, 'es'));
}

/**
 * The products with a price under the margin floor, and what selling at the floor's price would
 * have left more the last thirty days: each low price's presentation as sold on its list, returns
 * taken off. A price that did not sell is still low, at nothing a month. The most first.
 */
export function margenPorSubir(bajos: readonly PrecioBajo[], ventas: readonly Venta[], hoy: string): { productoId: string; alMes: number }[] {
  const desde = sumarDias(hoy, -(DIAS_DEL_MARGEN - 1));
  const vendidas = new Map<string, number>();
  for (const v of ventas) {
    if (v.anulada || v.fecha < desde || v.fecha > hoy) continue;
    const netos = netosDeLineas(v);
    for (const [i, l] of v.lineas.entries()) {
      if (!l.unidadVentaId) continue;
      const clave = `${l.unidadVentaId}|${l.listaId ?? v.listaId}`;
      vendidas.set(clave, (vendidas.get(clave) ?? 0) + netos[i]!.cantidad);
    }
  }
  const porProducto = new Map<string, number>();
  for (const b of bajos) {
    const unidades = (vendidas.get(`${b.unidadVentaId}|${b.listaId}`) ?? 0) / 1_000;
    porProducto.set(b.productoId, (porProducto.get(b.productoId) ?? 0) + Math.max(0, b.sugerido - b.precio) * unidades);
  }
  return [...porProducto].map(([productoId, alMes]) => ({ productoId, alMes: Math.round(alMes) })).sort((a, b) => b.alMes - a.alMes);
}

/**
 * The batches past their date or within the alert days (`tablero`), and what is left of them cost:
 * each at what its product is carried at where the batch is (`costoDe`). By product, the dearest
 * first.
 */
export function loQueVence(
  lotes: readonly { productoId: string; ubicacion: string; cantidadDisponible: Milli }[],
  costoDe: (productoId: string, ubicacion: string) => UnitCost,
): { productoId: string; valor: number }[] {
  const valor = new Map<string, number>();
  for (const l of lotes) {
    if (l.cantidadDisponible <= 0) continue;
    valor.set(l.productoId, (valor.get(l.productoId) ?? 0) + costOf(costoDe(l.productoId, l.ubicacion), l.cantidadDisponible));
  }
  return [...valor].map(([productoId, v]) => ({ productoId, valor: v })).sort((a, b) => b.valor - a.valor);
}

// ---------------------------------------------------------------------------
// ¿Cuánto creerle a las cifras?
// ---------------------------------------------------------------------------

/** The days the sales' doubts are read over. */
export const DIAS_DE_DUDA = 30;
/** A product counted within these days is counted. */
export const DIAS_CONTADO = 90;
/** Fewer days of sales than eight weeks, and the paces read over them are short. */
export const DIAS_DE_HISTORIA = 56;

export interface CategoriaContada {
  categoria: string;
  /** The last day a count took in any of its products; null if none ever did. */
  ultima: string | null;
  dias: number | null;
  /** Of its products with something on the shelf, how many a count took in the last `DIAS_CONTADO` days. */
  contados: number;
  productos: number;
}

export interface Confianza {
  /** The quick sales of the last thirty days: lines with no product, whose cost nobody knows, so all of it reads as profit. */
  rapidas: { monto: Centavos; ventas: number };
  /** Products sold the last thirty days at no cost: the books took nothing for what they cost. The most first. */
  sinCosto: { monto: Centavos; productos: { productoId: string; monto: number }[] };
  /** Places that hold less than nothing of a product, off the trucks: a count is waiting, as the home screen says. */
  negativos: number;
  /** Each category with something on the shelf, the longest without a count first, never before that. */
  categorias: CategoriaContada[];
  /** From the first sale to today, both in; null with no sale. */
  diasDeVentas: number | null;
}

const nombreDeCategoria = (p: Pick<Producto, 'categoria'>): string => p.categoria.trim() || 'Otros';

/**
 * What can keep a figure from being the real one: quick sales and products sold at no cost, which
 * read as all profit; stock under zero; categories long uncounted, where a loss goes unseen; and
 * few days of sales behind the paces.
 */
export function confianza(d: {
  ventas: readonly Venta[];
  productos: readonly Producto[];
  existencias: readonly Existencia[];
  conteos: readonly Conteo[];
  hoy: string;
  primeraVenta: string | null;
  /** A place that is a truck: its stock squares at the route's settlement, not at a count. */
  esCamion: (ubicacion: string) => boolean;
}): Confianza {
  const desde = sumarDias(d.hoy, -(DIAS_DE_DUDA - 1));
  let rapidas = 0;
  let conRapida = 0;
  const sinCosto = new Map<string, number>();
  for (const v of d.ventas) {
    if (v.anulada || v.fecha < desde || v.fecha > d.hoy) continue;
    const netos = netosDeLineas(v);
    let rapida = false;
    for (const [i, l] of v.lineas.entries()) {
      const n = netos[i]!;
      if (n.ingreso <= 0) continue;
      if (!l.productoId) {
        rapidas += n.ingreso;
        rapida = true;
      } else if (n.costo <= 0) sinCosto.set(l.productoId, (sinCosto.get(l.productoId) ?? 0) + n.ingreso);
    }
    if (rapida) conRapida += 1;
  }

  // What each product holds, everywhere but the trucks, and the last day a count took it in.
  const hay = new Map<string, number>();
  let negativos = 0;
  for (const e of d.existencias) {
    if (d.esCamion(e.ubicacion)) continue;
    if (e.cantidad < 0) negativos += 1;
    hay.set(e.productoId, (hay.get(e.productoId) ?? 0) + e.cantidad);
  }
  const contado = new Map<string, string>();
  for (const c of d.conteos) {
    if (c.fecha > d.hoy) continue;
    for (const l of c.lineas) if ((contado.get(l.productoId) ?? '') < c.fecha) contado.set(l.productoId, c.fecha);
  }
  const reciente = sumarDias(d.hoy, -(DIAS_CONTADO - 1));
  const porCategoria = new Map<string, CategoriaContada>();
  for (const p of d.productos) {
    if (!p.activo || (hay.get(p.id) ?? 0) <= 0) continue;
    const nombre = nombreDeCategoria(p);
    const x = porCategoria.get(nombre) ?? { categoria: nombre, ultima: null, dias: null, contados: 0, productos: 0 };
    const fecha = contado.get(p.id) ?? null;
    x.productos += 1;
    if (fecha !== null && fecha >= reciente) x.contados += 1;
    if (fecha !== null && (x.ultima === null || fecha > x.ultima)) x.ultima = fecha;
    porCategoria.set(nombre, x);
  }
  const categorias = [...porCategoria.values()]
    .map((x) => ({ ...x, dias: x.ultima === null ? null : diasEntre(x.ultima, d.hoy) }))
    .sort((a, b) => (b.dias ?? Infinity) - (a.dias ?? Infinity) || a.categoria.localeCompare(b.categoria, 'es'));

  return {
    rapidas: { monto: rapidas as Centavos, ventas: conRapida },
    sinCosto: {
      monto: [...sinCosto.values()].reduce((a, m) => a + m, 0) as Centavos,
      productos: [...sinCosto].map(([productoId, monto]) => ({ productoId, monto })).sort((a, b) => b.monto - a.monto),
    },
    negativos,
    categorias,
    diasDeVentas: d.primeraVenta === null || d.primeraVenta > d.hoy ? null : diasEntre(d.primeraVenta, d.hoy) + 1,
  };
}
